import { Fragment, useMemo, useState, useCallback } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PublishArticleModal } from './PublishArticleModal';
import {
  useGetArticlesQuery,
  useDeleteArticleMutation,
  useUpdateArticleStatusMutation,
} from '@/services/ArticleService';
import { Article } from '@/types/cms.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { toAbsoluteUrl } from '@/utils';
import { usePermissions } from '@/hooks/usePermissions';
import clsx from 'clsx';
import { PaginationState } from '@tanstack/react-table';
import { formatDisplayDateTime } from '@/utils/Date';

const ArticlesListing = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    articleId: string | null;
  }>({
    isOpen: false,
    articleId: null,
  });
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(
    null
  );

  const {
    data: articlesData,
    isLoading,
    refetch,
  } = useGetArticlesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  const [deleteArticle] = useDeleteArticleMutation();
  const [updateArticleStatus] = useUpdateArticleStatusMutation();

  const handleDelete = async () => {
    if (!deleteModal.articleId) return;

    try {
      await deleteArticle(deleteModal.articleId).unwrap();
      enqueueSnackbar('Article deleted successfully', { variant: 'success' });
      setDeleteModal({ isOpen: false, articleId: null });
      refetch();
    } catch {
      enqueueSnackbar('Failed to delete article', { variant: 'error' });
    }
  };

  const handleStatusUpdate = useCallback(
    async (
      id: string,
      status: 'published' | 'archived' | 'draft',
      publishedAt?: string | null
    ) => {
      // If changing to published and no publishedAt provided, show modal
      if (status === 'published' && publishedAt === undefined) {
        setSelectedArticleId(id);
        setPublishModalOpen(true);
        return;
      }

      try {
        // Build payload with publishedAt if provided
        const payload: { id: string; status: string; publishedAt?: string } = {
          id,
          status,
        };
        if (status === 'published' && publishedAt) {
          payload.publishedAt = publishedAt;
        }

        await updateArticleStatus(payload).unwrap();
        enqueueSnackbar(`Article ${status} successfully`, {
          variant: 'success',
        });
        refetch();
      } catch {
        enqueueSnackbar(`Failed to ${status} article`, { variant: 'error' });
      }
    },
    [updateArticleStatus, enqueueSnackbar, refetch]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'coverImageUrl',
        header: 'Image',
        cell: ({ row }: { row: { original: Article } }) => (
          <img
            src={
              row.original.coverImageUrl
                ? toAbsoluteUrl(row.original.coverImageUrl)
                : '/media/avatars/harbor-default.jpg'
            }
            className="size-10 rounded-lg object-cover"
            alt={row.original.title || 'Article'}
          />
        ),
        meta: { className: 'w-20' },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }: { row: { original: Article } }) => (
          <span className="font-medium text-gray-800">
            {row.original.title}
          </span>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }: { row: { original: Article } }) => (
          <span className="badge badge-sm badge-outline text-nowrap">
            {row.original.category}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: Article } }) => (
          <span
            className={clsx(
              'badge badge-sm capitalize',
              row.original.status === 'published'
                ? 'badge-success'
                : row.original.status === 'archived'
                  ? 'badge-warning'
                  : 'badge-info'
            )}
          >
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'statusActions',
        header: 'Status Actions',
        cell: ({ row }: { row: { original: Article } }) => (
          <div className="flex items-center gap-2 min-w-[120px]">
            <select
              value={row.original.status}
              onChange={(e) =>
                handleStatusUpdate(
                  row.original.id,
                  e.target.value as 'draft' | 'published' | 'archived'
                )
              }
              className="select select-sm select-bordered"
              disabled={!hasPermission('article_management_publish')}
              aria-label="Update article status"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        ),
        meta: { className: 'w-40' },
      },
      {
        accessorKey: 'publishedAt',
        header: 'Published At',
        cell: ({ row }: { row: { original: Article } }) => (
          <span className="text-gray-600">
            {row.original.status === 'published'
              ? row.original.publishedAt
                ? formatDisplayDateTime(row.original.publishedAt, {
                    hour12: false,
                  })
                : ''
              : '-'}
          </span>
        ),
        meta: { className: 'w-48' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: Article } }) => (
          <div className="flex gap-2">
            {hasPermission('article_management_view_versions') && (
              <button
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  navigate(`/article-management/${row.original.id}/versions`)
                }
                title="Versions"
                aria-label="Versions"
              >
                <KeenIcon icon="data" />
              </button>
            )}
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() =>
                navigate(`/article-management/view/${row.original.id}`)
              }
              title="View"
              aria-label="View"
            >
              <KeenIcon icon="eye" />
            </button>
            {hasPermission('article_management_update') && (
              <button
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  navigate(`/article-management/edit/${row.original.id}`)
                }
                title="Edit"
                aria-label="Edit"
              >
                <KeenIcon icon="pencil" />
              </button>
            )}
            {hasPermission('article_management_delete') && (
              <button
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  setDeleteModal({
                    isOpen: true,
                    articleId: row.original.id,
                  })
                }
                title="Delete"
                aria-label="Delete"
              >
                <KeenIcon icon="trash" className="text-danger" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-32' },
      },
    ],
    [navigate, hasPermission, handleStatusUpdate]
  );

  return (
    <Fragment>
      <section
        aria-labelledby="articles-management-heading"
        aria-label="Articles"
      >
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle
                  id="articles-management-heading"
                  text="Articles"
                />
                <ToolbarDescription>
                  Manage your CMS articles
                </ToolbarDescription>
              </ToolbarHeading>
              <ToolbarActions>
                {hasPermission('article_management_create') && (
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => navigate('/article-management/create')}
                  >
                    Add New Article
                  </button>
                )}
              </ToolbarActions>
            </Toolbar>
          </Container>
        )}

        <div className="container-fixed">
          <div className="card">
            <div className="card-body">
              <DataGrid
                columns={columns}
                data={articlesData?.data || []}
                loading={isLoading}
                serverSide
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: articlesData?.totalItems || 0,
                  totalPages: articlesData?.totalPages || 0,
                  setPagination,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <ConfirmationDialog
        open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, articleId: null })}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this article? This action cannot be undone."
      />

      <PublishArticleModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={(publishedAt) => {
          if (selectedArticleId) {
            handleStatusUpdate(selectedArticleId, 'published', publishedAt);
          }
          setPublishModalOpen(false);
          setSelectedArticleId(null);
        }}
      />
    </Fragment>
  );
};

export { ArticlesListing };
